import { useEffect, useState } from "react";
import { UserRound, Loader2, FileDown, MapPin, BadgeCheck, ArrowLeft, Pencil } from "lucide-react";
import api from "@/lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { fmtDur } from "./ClientStayDialog";
import { StayCard, SignatureDialog, openStayReport, fmtD } from "./StayHistory";
import { FamilyAccess } from "./FamilyAccess";
import { NotesPanel } from "@/components/notes/NotesPanel";
import { ContactsEditor } from "./ContactsEditor";
import { tabCls } from "@/components/profile/ProfileBits";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const age = (dob) => (dob ? Math.floor((Date.now() - new Date(dob)) / 31557600000) : null);
const panelCls = "mt-4 data-[state=inactive]:hidden";

export function ClientProfileView({ clientId, onBack, onEdit, backLabel = "Back to clients", refreshKey = 0 }) {
  const [client, setClient] = useState(null);
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [stays, setStays] = useState(null);
  const [sig, setSig] = useState(null);

  useEffect(() => {
    api.get("/clients", { params: { include_archived: true } }).then(({ data }) => setClient(data.find((c) => c.id === clientId) || null));
    api.get("/stays", { params: { client_id: clientId } }).then(({ data }) => setStays(data)).catch(() => setStays([]));
  }, [clientId, refreshKey]);
  const showSig = async (s) => { const { data } = await api.get(`/stays/${s.id}/signature`); setSig({ ...data, stay: s }); };
  const totalMin = (stays || []).reduce((a, s) => a + (s.duration_minutes ?? s.minutes_so_far ?? 0), 0);

  return (
    <div className="space-y-4" data-testid="client-profile-page">
      {onBack && (
        <button onClick={onBack} data-testid="client-profile-back" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 transition">
          <ArrowLeft className="h-4 w-4" /> {backLabel}
        </button>
      )}
      <header className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-start gap-4">
        <div className="h-20 w-20 shrink-0 rounded-xl bg-slate-100 overflow-hidden flex items-center justify-center text-slate-400">
          {client?.photo_data_url ? <img src={client.photo_data_url} alt="" className="h-full w-full object-cover" /> : <UserRound className="h-9 w-9" />}
        </div>
        <div className="flex-1 min-w-0">
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 truncate" data-testid="client-profile-name">{client?.full_name || "Loading…"}</h1>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 mt-1.5">
            {client?.dob && <span>Born {fmtD(client.dob)} · {age(client.dob)} yrs</span>}
            {client?.address && <span className="inline-flex items-center gap-1" data-testid="client-profile-address"><MapPin className="h-3 w-3" /> {client.address}{client.address_verified && <BadgeCheck className="h-3 w-3 text-emerald-600" title="Verified with Google" />}</span>}
            {client && <span>Expected {client.schedule_days?.length ? [...client.schedule_days].sort().map((d) => DAYS[d]).join(", ") : "—"}</span>}
            {client && !client.active && <span className="rounded-md bg-amber-100 px-2 py-0.5 font-semibold text-amber-800" data-testid="client-inactive-chip">Archived · read-only</span>}
            {client?.on_site && <span className="rounded-full bg-emerald-100 px-2 py-0.5 font-semibold text-emerald-800">On site now</span>}
          </div>
        </div>
        {client && client.active && onEdit && (
          <button onClick={() => onEdit(client)} data-testid="client-profile-edit-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-100 transition">
            <Pencil className="h-4 w-4" /> Edit
          </button>
        )}
      </header>

      {!client ? <div className="py-16 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
        <Tabs defaultValue="overview">
          <TabsList className="bg-slate-100 rounded-lg p-1 h-auto flex-wrap justify-start">
            <TabsTrigger value="overview" className={tabCls} data-testid="client-tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="stays" className={tabCls} data-testid="client-tab-stays">Stay history ({stays?.length ?? 0})</TabsTrigger>
            <TabsTrigger value="notes" className={tabCls} data-testid="client-tab-notes">Notes</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className={panelCls}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <section className="rounded-xl border border-slate-200 bg-white p-4" data-testid="client-profile-needs">
                <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500 mb-1">Care needs</div>
                <div className="text-sm text-slate-800 whitespace-pre-line">{client.needs || <span className="text-slate-400">None recorded</span>}</div>
                {client.notes && <div className="mt-2 text-xs text-slate-500 whitespace-pre-line"><b>Internal notes:</b> {client.notes}</div>}
              </section>
              <ContactsEditor clientId={clientId} contacts={client.contacts || []} onSaved={(contacts) => setClient({ ...client, contacts })} />
              <FamilyAccess clientId={clientId} />
            </div>
          </TabsContent>

          <TabsContent value="stays" className={panelCls}>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
              <div className="text-[11px] font-semibold uppercase tracking-widest text-slate-500">Stay history <span className="normal-case tracking-normal text-slate-400 font-normal" data-testid="client-profile-totals">· {stays?.length ?? 0} stays · {fmtDur(totalMin)} total</span></div>
              <div className="flex items-center gap-1.5">
                <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="report-month" className="h-8 rounded-md border border-slate-200 bg-white px-2 text-xs outline-none focus:border-primary" />
                <button onClick={() => openStayReport(clientId, month)} data-testid="client-report-pdf" className="inline-flex items-center gap-1.5 rounded-md bg-slate-900 px-3 h-8 text-xs font-semibold text-white hover:bg-slate-800 transition">
                  <FileDown className="h-3.5 w-3.5" /> Monthly report PDF
                </button>
              </div>
            </div>
            <div className="space-y-2" data-testid="client-profile-history">
              {stays === null && <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />}
              {stays?.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 p-6 text-center text-sm text-slate-400">No stays recorded yet.</div>}
              {stays?.map((s) => <StayCard key={s.id} s={s} onSignature={showSig} />)}
            </div>
          </TabsContent>

          <TabsContent value="notes" className={panelCls}>
            <div className="rounded-xl border border-slate-200 bg-white p-4" data-testid="client-notes"><NotesPanel subject={{ type: "client", id: clientId }} /></div>
          </TabsContent>
        </Tabs>
      )}
      <SignatureDialog sig={sig} onClose={() => setSig(null)} />
    </div>
  );
}
